import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, CheckCircle2, Brain, Shield, 
  Archive, Download, Clock, TrendingUp, AlertTriangle
} from "lucide-react";
import { generateWorkflowOnePager } from "@/services/workflowDocService";
import { toast } from "sonner";
import { LiveStatsWidget } from "@/components/demo/LiveStatsWidget";

const DemoWorkflow = () => {
  const navigate = useNavigate();

  const handleDownloadOnePager = () => {
    generateWorkflowOnePager("AthleteNexus");
    toast.success("One-pager downloaded successfully");
  };

  const workflowSteps = [
    {
      id: 1,
      title: "Deal Intake",
      icon: FileText,
      description: "Athletes submit NIL deals through the portal with contract details, brand information, and deliverables",
      duration: "< 5 minutes",
      color: "text-primary"
    },
    {
      id: 2,
      title: "AI Analysis",
      icon: Brain,
      description: "Automated compliance analysis using Lovable AI (Gemini 2.5 Flash) assesses risk, flags prohibited activities, and generates recommendations",
      duration: "Real-time (< 30 seconds)",
      color: "text-accent"
    },
    {
      id: 3,
      title: "Conflict Detection",
      icon: AlertTriangle,
      description: "System checks against institutional sponsors, prohibited categories, and existing deals for potential conflicts",
      duration: "Real-time (< 10 seconds)",
      color: "text-warning"
    },
    {
      id: 4,
      title: "Approval Workflow",
      icon: CheckCircle2,
      description: "Compliance officers review with AI insights, approve/reject with evidence view, and assign risk levels",
      duration: "Avg: 2.3 hours",
      color: "text-success"
    },
    {
      id: 5,
      title: "Archive & Audit",
      icon: Archive,
      description: "Approved deals archived with hash-chain verification, creating immutable audit trail for NCAA compliance",
      duration: "Automatic",
      color: "text-secondary"
    },
    {
      id: 6,
      title: "Export & Reports",
      icon: Download,
      description: "Generate NCAA-ready reports in CSV, Excel, or PDF formats with complete compliance documentation",
      duration: "< 1 minute",
      color: "text-muted-foreground"
    }
  ];


  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border/50">
        <div className="max-w-[1400px] mx-auto px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="text-sm font-light tracking-wide text-foreground hover:text-primary transition-colors"
          >
            ← AthleteNexus
          </button>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/workflow-docs")}
            >
              Full Documentation
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate("/setup-demo")}
            >
              Try Demo Environment
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-8">
        <div className="max-w-[1200px] mx-auto text-center">
          <Badge variant="secondary" className="mb-4">
            Live Demo
          </Badge>
          <h1 className="text-5xl font-light leading-tight tracking-tight text-foreground mb-6">
            See AthleteNexus in Action
          </h1>
          <p className="text-xl font-light text-muted-foreground max-w-[700px] mx-auto mb-8">
            Watch how our automated compliance workflow processes NIL deals from submission to approval in under 3 hours
          </p>

          {/* Video Placeholder */}
          <div className="max-w-[900px] mx-auto mb-12">
            <Card className="p-8 bg-muted/30 border-border">
              <div className="aspect-video bg-card rounded-lg flex items-center justify-center border border-border">
                <div className="text-center">
                  <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-10 h-10 text-primary"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                    </svg>
                  </div>
                  <p className="text-lg font-medium text-foreground mb-2">
                    3-Minute Workflow Walkthrough
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Video coming soon • Live demo available now
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Live Stats Widget */}
          <div className="max-w-[900px] mx-auto">
            <LiveStatsWidget />
          </div>
        </div>
      </section>

      {/* Workflow Diagram */}
      <section className="py-16 px-8 bg-muted/30">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-3xl font-light text-foreground text-center mb-12">
            Complete Workflow in 6 Steps
          </h2>

          <div className="space-y-8">
            {workflowSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={step.id} className="relative">
                  <Card className="p-6 hover:shadow-lg transition-all border-border">
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-card border border-border rounded-full flex items-center justify-center">
                          <Icon className={`w-6 h-6 ${step.color}`} />
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-xl font-medium text-foreground">
                            Step {step.id}: {step.title}
                          </h3>
                          <Badge variant="outline" className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {step.duration}
                          </Badge>
                        </div>
                        <p className="text-muted-foreground leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </Card>
                  {index < workflowSteps.length - 1 && (
                    <div className="flex justify-start ml-6 my-4">
                      <div className="w-px h-8 bg-border" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-8">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-3xl font-light text-foreground text-center mb-12">
            Why Athletic Departments Choose AthleteNexus
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 border-border">
              <Shield className="w-10 h-10 text-primary mb-4" />
              <h3 className="text-lg font-medium mb-2">NCAA Compliance Guarantee</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Automated checks against all NCAA rules, conference regulations, and state laws with real-time updates
              </p>
            </Card>

            <Card className="p-6 border-border">
              <TrendingUp className="w-10 h-10 text-success mb-4" />
              <h3 className="text-lg font-medium mb-2">98.5% Approval Rate</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                AI-powered pre-screening catches issues before submission, ensuring smooth approval process
              </p>
            </Card>

            <Card className="p-6 border-border">
              <Clock className="w-10 h-10 text-accent mb-4" />
              <h3 className="text-lg font-medium mb-2">2.3 Hour Avg Review</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Fastest compliance review in the industry with automated workflows and intelligent routing
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-8 bg-muted/30">
        <div className="max-w-[800px] mx-auto text-center">
          <h2 className="text-3xl font-light text-foreground mb-6">
            Ready to Experience the Workflow?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Try our fully-functional demo environment with 20 pre-analyzed deals, or download our one-pager for a detailed overview
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate("/setup-demo")}
              className="h-12 px-8"
            >
              Launch Demo Environment
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={handleDownloadOnePager}
              className="h-12 px-8"
            >
              <Download className="w-4 h-4 mr-2" />
              Download One-Pager
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-6">
            Or{" "}
            <button
              onClick={() => navigate("/workflow-docs")}
              className="text-primary hover:underline"
            >
              view full documentation
            </button>
            {" "}with SLAs, retention policies, and role permissions
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-8 border-t border-border">
        <div className="max-w-[1200px] mx-auto text-center text-sm text-muted-foreground">
          <p>© 2025 AthleteNexus. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default DemoWorkflow;
